import { MortgageCalculator } from "@component/Mortgage/Calculator/MortgageCalculator";
import type { FAQSchema } from "@component/StructuredDataSchema";
import { StructuredDataSchema } from "@component/StructuredDataSchema";
import { YoutubeVideo } from "@component/Widget/YoutubeVideo/YoutubeVideo";
import {
	faShuffle as farShuffle,
	faQuestion as farQuestion,
	faHouseHeart as farHouseHeart,
	faBabyCarriage as farBabyCarriage,
	faBaby as farBaby,
	faHouse as farHouse,
	faHandHoldingDollar as farHandHoldingDollar,
	faPaintRoller as farPaintRoller,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCalculator as fasCalculator,
	faPhone as fasPhone,
	faPaste as fasPaste,
	faFileSignature as fasFileSignature,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BankLogosCloud } from "@landing/components/BankLogosCloud";
import { CsokPlusComponent } from "@landing/components/CsokPlusComponent";
import { LandingAccordion } from "@landing/components/LandingAccordion";
import { LandingBottom } from "@landing/components/LandingBottom";
import { ExpertSlider } from "@ui/components/ExpertSlider";
import { InfoCard } from "@ui/components/InfoCard";
import { FeatureBox, LandingContainer, LandingHowTo, LandingHowToItem } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Accordion, Button, Card, Col, Container, Row } from "react-bootstrap";

/**
 * @Routing
 *
 * @title "Hitelkiváltás kalkulátor [YEAR]"
 * @description "Cseréld meglévő lakáshiteled kedvezőbbre! A megfelelő lakáshitel kiválasztása után, segítünk a hitel igénylés teljes folyamatában."
 * @route "/lakashitel-hitelkivaltas/"
 */
export default class Landing extends React.PureComponent {
	public render(): ReactNode {
		return (
			<>
				<div id="kalkulator">
					<MortgageCalculator
						title={
							<>
								<span className="d-md-none display-4 font-weight-black">Hitelkiváltás kalkulátor</span>
								<span className="d-none d-md-inline">Hitelkiváltás kalkulátor</span>
							</>
						}
						description={
							<>
								<span className="d-md-none">Spórolj milliókat!</span>
								<span className="d-none d-md-inline">
									Hitelkiváltással meglévő jelzáloghiteled kedvezőbbre cserélheted.
								</span>
							</>
						}
						variant="HITELKIVALTAS"
					/>
				</div>
				<CsokPlusComponent />
				<Container>
					<InfoCard variant="retail" className="text-white">
						<Card.Body>
							<strong>Ha nem jelzáloghitelt</strong>, hanem fedezetlen kölcsönt vagy kölcsönöket{" "}
							<strong>szeretnél rendezni</strong> (például folyószámlahitel, áruhitel, hitelkártya,
							személyi kölcsön), akkor <strong>használd az </strong>
							<a href="/adossagrendezo-hitel/?link_type=info-card">
								<strong>Adósságrendező hitel kalkulátort</strong>
							</a>
							!
						</Card.Body>
					</InfoCard>
				</Container>

				<LandingContainer>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Szakértőink, akik Neked segítenek</h2>
					<ExpertSlider groupName="BM_HQ_MORTGAGE" />
				</LandingContainer>
				<LandingContainer size="lg" bgClassName="bg-gray">
					<LandingHowTo title="Így cserélheted kedvezőbbre meglévő drága jelzáloghiteled">
						<LandingHowToItem title="Kalkuláció" icon={<FontAwesomeIcon icon={fasCalculator} />}>
							Versenyeztesd meg a bankok ajánlatait, és válaszd ki a számodra legkedvezőbbet.
						</LandingHowToItem>
						<LandingHowToItem title="Érdeklődés" icon={<FontAwesomeIcon icon={fasPhone} />}>
							Add le érdeklődésed a választott lakáshitelre, és szakértőnk visszahív.
						</LandingHowToItem>
						<LandingHowToItem title="Ügyintézés" icon={<FontAwesomeIcon icon={fasPaste} />}>
							Ha kiválasztottad a számodra megfelelő bankot, segítünk az ügyintézésben is.
						</LandingHowToItem>
						<LandingHowToItem
							title="Megtörténik a hitelkiváltás"
							icon={<FontAwesomeIcon icon={fasFileSignature} />}
						>
							Az új bank rendezi meglévő tartozásodat. Innentől már kedvezőbb lesz a törlesztés.
						</LandingHowToItem>
					</LandingHowTo>
				</LandingContainer>
				<LandingContainer>
					<BankLogosCloud
						variant="mortgage"
						title="Hitelkiváltás kalkulátorunk az alábbi bankok termékeit tartalmazza"
					/>
				</LandingContainer>
				<LandingContainer>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">Amit tudni érdemes a hitelkiváltásról</h2>
					<Accordion>
						<LandingAccordion title="Mi az a hitelkiváltás?">
							<p>
								A hitelkiváltás során meglévő hitelünket vagy hiteleinket egy új, kedvezőbb kölcsönre
								cseréljük le. Lényege, hogy a cserével jobb helyzetbe kerüljünk, ami lehet akár
								biztonságosabb konstrukció, alacsonyabb törlesztőrészlet, rövidebb futamidő, esetleg
								átláthatóbb, könnyebben kezelhető tartozás.
							</p>
							<p>
								A régi és az új hitel lehet egyazon banknál, de ez nem feltétel. A bankok kínálnak
								kifejezetten hitelkiváltás céljára jelzáloghitelt, személyi kölcsönt és{" "}
								<a href="/babavaro-hitel/?link_type=landing-faq">Babaváró hitelt</a>, ami azért lehet
								előnyös, mert így a kiváltandó hitel havi törlesztőjét már nem veszik figyelembe a{" "}
								<a href="/jobb-tudni/babavaro/#jovedelemaranyos-torlesztoreszlet-mutato-jtm/?link_type=landing-faq">
									JTM
								</a>{" "}
								(jövedelemarányos törlesztőrészlet mutató) számítás során, hiszen azt a kiváltást
								követően már nem kell fizetni. Ezáltal egy korábban esetleg elvesztett hitelképesség is
								visszaállítható.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Kinek és miért éri meg a hitelkiváltás?">
							<strong>Nagyobb biztonság</strong>
							<p>
								Amennyiben valaki változó kamatozású (vagyis egy éves vagy ennél rövidebb
								kamatperiódusú) hitellel rendelkezik – mint például a forintosított devizahitelesek
								jelentős része –, az hitelkiváltással csökkentheti a kamatkockázatát azzal, hogy
								hosszabb időre fixált kamatú hitelre vált. Ebben az esetben a havi törlesztő nem
								feltétlenül fog csökkenni, viszont az adós biztos lehet benne, hogy a kamatperiódusok
								alatt a hitelkamat, ezzel pedig a havi fizetnivaló nem emelkedhet.
							</p>
							<p>
								A változó kamatozású lakáshitellel rendelkezőket jelenleg a kamatstop védi, ugyanakkor
								érdemes még a védelem lejárta előtt utánajárni, lehet-e kedvezőbbre, biztonságosabbra
								váltani. Ebben segít a hitelkiváltás kalkulátor.
							</p>
							<strong>Alacsonyabb havi törlesztő</strong>
							<p>
								Az elmúlt időszakban a hitelkamatok drágultak, de már akad olyan pénzintézet, amely
								csökkentette lakáshitele kamatát. Könnyen lehet, hogy a meglévő hitelnél találhatunk
								kedvezőbbet. Egyébként is érdemes 1-2 évente körülnézni a hitelpiacon, megvizsgálni
								meglévő hiteleinket és ellenőrizni, hogy egy hitelkiváltással mekkora pénzügyi előnyre
								tehetünk szert.
							</p>
							<strong>Adósságrendezés</strong>
							<p>
								Előfordul, hogy valaki több hitellel (személyi kölcsön, áruhitel, hitelkártya tartozás)
								rendelkezik, így a végén már nehezen látja át, pontosan milyen kamatokat fizet, a
								kondíciók mennyire kedvezőek. Ilyen esetben megfontolandó az adósságrendezés, vagyis a
								meglévő hitelek kiváltása egyetlen hitellel. Ezzel legtöbbször a hiteltörlesztésre
								fordított összeg is lecsökkenthető, vagyis havi szinten megtakarítás érhető el.
							</p>
							<strong>Hitelképesség visszaállítása</strong>
							<p>
								Amennyiben meglévő hiteleinket kedvezőbb kamatozásúra cseréljük, ezzel pedig a
								hiteltörlesztésre fordítandó összeg csökken, akkor az adós hitelképessége is
								visszaállítható.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Hitelkiváltás ingatlanfedezettel?">
							<p>
								Amennyiben jelzáloghitellel szeretnénk meglévő kölcsöneinket kiváltani, akkor ehhez
								szükség lesz megfelelő ingatlanfedezet bevonására a hitelszerződésbe. A fedezetként
								bevont ingatlan becsült forgalmi értékének legfeljebb 80%-a lehet a hitelösszeg, de a
								banki gyakorlat inkább a 60-70%-hoz áll közelebb.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Hitelkiváltás ingatlanfedezet nélkül?">
							<p>
								Ha <a href="/szemelyi-kolcson/?link_type=landing-faq">személyi kölcsönnel</a> vagy
								Babaváró hitellel váltanánk ki meglévő hitelünket/hiteleinket, akkor nem lesz szükség
								ingatlanfedezetre.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Hitelkiváltás költségei?">
							<p>
								A hitelkiváltás költségei két részből tevődnek össze: a kiváltandó hitel
								végtörlesztésének díjából, valamint az új hitel költségeiből. A végtörlesztési díj
								bankonként és hiteltípusonként eltérő mértékű, de átlagosan a tőketartozás 1-2%-ával
								lehet számolni.
							</p>
							<p>
								Egy <a href="/lakashitel-kalkulator/?link_type=landing-faq">jelzáloghitelnél</a> a
								közjegyzői és értékbecslési díjjal együtt több százezer forint is lehet az egyszeri
								induló költség. A bankok azonban sok esetben elengedik, vagy visszatérítik ezek jelentős
								részét, ezért választáskor erre is érdemes odafigyelni.{" "}
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Hitelkiváltás menete">
							<p>
								A hitelkiváltás fontos feltétele, hogy az igénylő hitelképes legyen, vagyis rendelkezzen
								megfelelő összegű igazolt jövedelemmel. Amennyiben lakáshitellel (vagyis
								jelzáloghitellel) váltaná ki meglévő hitelét, akkor fontos a megfelelő ingatlanfedezet
								megléte, ha személyi kölcsönt vagy Babaváró hitelt igényelne ezzel a céllal, akkor erre
								természetesen nincs szükség.
							</p>
							<p>
								A hitelkiváltást tulajdonképpen nem más, mint egy új hitelfelvétel, ahol a hitelcél egy
								vagy több meglévő kölcsön kifizetése. Ezért az igénylés menete megegyezik egy
								lakáshitel, egy személyi kölcsön vagy éppen egy Babaváró hitel felvételének menetével.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="A hitelkiváltás átfutási ideje">
							<p>
								Amennyiben jelzáloghitelt igényelnénk kiváltásra, akkor ennek átlagos átfutási ideje
								1-1,5 hónap, míg a személyi kölcsönnél ugyanez néhány nap, de legfeljebb 1-2 hét.
								Babaváró hitelnél a bankoknak 10 munkanapjuk van a benyújtott igény elbírálására.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Hitelkiváltás KHR listásoknak">
							<p>
								Az aktív <a href="jobb-tudni/babavaro/#khr-lista-bar-lista">KHR listás</a> adósok (más
								néven a BAR listások) nem hitelképesek, így számukra a hitelkiváltás nem lehetséges.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Babaváró támogatás hitelkiváltásra">
							<p>
								Bár a Babaváró hitel szabad felhasználású, valójában a bankok maguk döntik el, hogy ezen
								a téren megszabnak-e a korlátozást. Vagyis, aki az első útjába kerülő banknál igényel
								ilyen támogatott hitelt, az könnyen szembesülhet azzal, hogy nem kap arra pénzt, amire
								szeretne. Gyakori például a hitelkiváltás korlátozása az “ingyen” hitelből.
							</p>
							<p>
								A Bankmonitor gyűjtése szerint a következő bankok engedélyezik kiváltási céllal felvenni
								a <a href="/babavaro-hitel/?link_type=landing-faq">Babaváró támogatást</a>: CIB Bank,
								Erste Bank, OTP Bank, UniCredit Bank. Azonban a hitelkiváltást lehetővé tevő bankok
								gyakorlata sem egységes. Az Erste például csak a személyi kölcsönöket és a
								jelzáloghiteleket engedi kifizetni, míg a CIB-nél ezeken felül az autólízing és a
								gépjármű hitel esetében is élhetnek az adósok a hitelkiváltás lehetőségével.
							</p>
							<p>
								Az UniCredit még megengedőbb, náluk a folyószámlahitelt, a hitelkártya tartozást, az
								autó és ingatlan lízinget, a gyűjtőszámlahitelt, sőt a munkáltatói és az önkormányzati
								kölcsönt is ki lehet váltani Babaváróval. Az OTP Bank pedig a bank saját hiteleinél
								engedélyezi csak a kiváltást, mint hitelcélt.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Autóhitel kiváltása">
							<p>
								Akár <a href="/autohitel/?link_type=landing-faq">autóhitel</a> vagy áruhitel is
								kiváltható személyi kölcsönnel, lakáshitellel vagy Babaváró hitellel. Az, hogy ezek
								közül melyik elérhető, illetve melyik jelenti az optimális megoldást, az igénylő egyedi
								helyzetétől függ.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="A hitelkiváltás kalkulátor működése">
							<p>
								A Bankmonitor Hitelkiváltás Kalkulátora meglévő hitelünk vagy hiteleink adatai alapján
								pontos információval szolgál arra nézve, hogy jelenleg találhatunk-e kedvezőbb kamatú
								vagy biztonságosabb jelzálog hitelterméket helyette.
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Az adósságrendező kalkulátor működése">
							<p>
								A Bankmonitor Adósságrendező hitel kalkulátorával meglévő fedezetlen (nem jelzálog
								alapú) hitelünket vagy hiteleinket válthatjuk ki, vonhatjuk össze. Ha tehát nem
								rendelkezel jelzáloghitellel, vagy nem jelzáloghiteledet szeretnéd kiváltani, hanem
								egyéb kölcsöneidet (például áruhitel, folyószámla hitelkeret, személyi kölcsön,
								hitelkártya tartozás), akkor használd az Adósságrendező hitel kalkulátort!
							</p>
						</LandingAccordion>
					</Accordion>
					<Accordion>
						<LandingAccordion title="Mely bankok kínálnak jelzáloghitelt kiváltásra?">
							<p>
								A{" "}
								<a href="/lakashitel-kalkulator/?link_type=landing-faq">
									Bankmonitor lakáshitel kalkulátor
								</a>{" "}
								alapján jelenleg a következő bankok kínálnak lakáscélú hitel kiváltására jelzáloghitelt:
								K&H Bank, Erste Bank, CIB Bank, Raiffeisen Bank, MagNet Bank, UniCredit Bank, MBH Bank,
								OTP Bank.
							</p>
						</LandingAccordion>
					</Accordion>
				</LandingContainer>
				<LandingContainer
					className="text-center"
					bgClassName="bg-trianglify-orange bg-border-y"
					bgColor="f77809"
				>
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-white">
						Nézd meg, hogy mennyit spórolhatsz lakáscélú hitelkiváltással a Bankmonitor hitelkalkulátorával!
					</h2>
					<Button variant="light" size="lg" className="mt-3" href="#kalkulator" target="_blank">
						Kalkulálok
					</Button>
				</LandingContainer>
				<LandingContainer size="lg" bgClassName="bg-light">
					<h2 className="h1 mb-lg-4 mb-md-3 mb-2 text-center">
						További <mark className="bg-light text-secondary">hitel- és támogatás</mark> kalkulátorok
					</h2>
					{this.renderCards()}
				</LandingContainer>
				{this.renderVideos()}
				<LandingBottom tags="lakashitel" />
				<StructuredDataSchema schema={this.getSchema()} />
			</>
		);
	}
	private renderCards(): ReactNode {
		const colProps = {
			className: "my-3 align-items-stretch",
			lg: 3,
			md: 4,
			sm: 6,
		};

		return (
			<Row>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Adósságrendező <br className="d-sm-block d-none" />
								hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farShuffle} />}
						href="/adossagrendezo-hitel/"
						buttonText="Kalkuláció indítása"
					>
						Derítsd ki, hogy van-e kedvezőbb meglévő fedezetlen (nem jelzálog) kölcsönödnél!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Mennyi lakáshitelt <br className="d-sm-block d-none" />
								kaphatok?
							</>
						}
						icon={<FontAwesomeIcon icon={farQuestion} />}
						href="/lakashitel-kalkulator/mennyi-lakashitelt-kaphatok/"
						buttonText="Kalkuláció indítása"
					>
						Tudd meg, hogy jövedelmed és meglévő hiteleid alapján maximálisan mekkora összegű lakáshitelt
						kaphatsz.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Otthonteremtési <br className="d-sm-block d-none" />
								támogatás
							</>
						}
						icon={<FontAwesomeIcon icon={farHouseHeart} />}
						href="/otthonteremtesi-es-otthonfelujitasi-tamogatasok/"
						buttonText="Kalkuláció indítása"
					>
						Tudj meg többet lakásáfa kedvezmény, lakásfelújítási támogatás, CSOK és Babaváró támogatás
						témakörben!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Babaváró <br className="d-sm-block d-none" />
								hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farBabyCarriage} />}
						href="/babavaro-hitel/"
						buttonText="Kalkuláció indítása"
					>
						Számítsd ki hitelkalkulátorunkkal, hogy mennyi Babaváró hitelre vagy jogosult!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								CSOK <br className="d-sm-block d-none" />
								támogatás
							</>
						}
						icon={<FontAwesomeIcon icon={farBaby} />}
						href="/csok-feltetelei/"
						buttonText="Kalkuláció indítása"
					>
						Tudd meg, mennyi CSOK támogatást kaphatsz lakásvásárlásra, építésre vagy bővítésre!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Szabad <br className="d-sm-block d-none" />
								felhasználású hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farHouse} />}
						href="/lakashitel-kalkulator/szabad-felhasznalasu-jelzaloghitel-kalkulator/"
						buttonText="Kalkuláció indítása"
					>
						Szabad célú jelzáloghitelek akár 100 millió Ft hitelösszegig. Kalkulálj és találd meg a
						legkedvezőbb ajánlatokat!
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Személyi <br className="d-sm-block d-none" />
								kölcsön
							</>
						}
						icon={<FontAwesomeIcon icon={farHandHoldingDollar} />}
						href="/szemelyi-kolcson/"
						buttonText="Kalkuláció indítása"
					>
						14 bank szabad felhasználású, fix kamatú személyi kölcsöneinek összehasonlítása pillanatok
						alatt.
					</FeatureBox>
				</Col>
				<Col {...colProps}>
					<FeatureBox
						title={
							<>
								Támogatott <br className="d-sm-block d-none" /> felújítási hitel
							</>
						}
						icon={<FontAwesomeIcon icon={farPaintRoller} />}
						href="/lakasfelujitasi-tamogatas/"
						buttonText="Kalkuláció indítása"
					>
						Akár 6 millió Ft kedvezményes hitel az otthonfelújítási támogatás előfinanszírozására. Kalkulálj
						a részletekért!
					</FeatureBox>
				</Col>
			</Row>
		);
	}

	private renderVideos(): ReactNode {
		return (
			<LandingContainer as="aside">
				<div className="headline">
					<h2 className="h6 text-uppercase">Kapcsolódó videók</h2>
				</div>
				<Row className="justify-content-center">
					<Col lg={6}>
						<YoutubeVideo
							title="Mi a hitelkiváltás? Mik az előnyök és hátrányok 2023-ban?"
							videoId="fXqsQrYHyIs"
							description="Hitelkiváltáskor elsődleges cél, hogy jobb anyagi helyzetbe kerüljünk. Videónkból a hitelkiváltás részleteit ismerheted meg Argyelán József, a Bankmonitor vezető elemzőjének tolmácsolásában: Egyáltalán mit jelent a hitelkiváltás? Mik lehetnek az előnyei, és milyen buktatókat rejthet? Mire fordítható a hitelkiváltásra felvett kölcsön? Megéri a mostani gazdasági helyzetben lecserélni egy kedvezőtlen kamatozású jelzálog-, vagy lakáshitelt? Mennyit és mi mindent nyerhetek a cserével? Nézd meg a videót és tudd meg, hogy mit érdemes 2023-ban tudni a hitelkiváltásról, adósságrendezésről."
							uploadDate="2023-08-04"
						/>
					</Col>
					<Col lg={6}>
						<YoutubeVideo
							title="Változó lakástámogatások: több (tíz)milliót nyerhetsz a jó időzítéssel"
							videoId="dvFq2mLJcZo"
							description="2024-től a CSOK nem lesz elérhető 5000 főnél több lakossal rendelkező településen. Ez önmagában – a család helyzetétől függően – 600 ezer forinttól 10 millió forintig terjedő veszteséget jelent. Ha elveszik a CSOK lehetőség, akkor megszűnik továbbá a CSOK-hitel, az illetékmentesség és (új lakásoknál) az ÁFA visszafizetési kedvezmény is. A Babaváró hitelt érintő változásokkal együtt ez összességében több millió, esetenként több tízmillió forinttal növeli meg a lakásvásárlás teljes költségét. A jó döntéshez elengedhetetlen információkért nézd meg a videót!"
							uploadDate="2023-07-05"
						/>
					</Col>
				</Row>
			</LandingContainer>
		);
	}

	private getSchema(): FAQSchema {
		return {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: [
				{
					"@type": "Question",
					name: "Melyek a lakáshitel legfontosabb feltételei?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Az igényléshez szükséges egy megfelelő értékű ingatlanfedezet bevonása. A fedezet lehet maga a megvásárolni kívánt ingatlan, de akár más ingatlan is szóba jöhet. Ezek kívül szükséges igazolnia az igénylőnek azt is, hogy van jövedelme, valamint azt, hogy legalább 3 hónapja dolgozik egyazon munkahelyen, és már nem próbaidős.",
					},
				},
				{
					"@type": "Question",
					name: "Mitől függ, hogy mennyi lakáshitelt kaphatok?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A felvehető lakáshitel összege függ a megvásárolni kívánt ingatlan értékétől, valamint az igénylő által igazolt jövedelem nagyságától. A hitel összege maximum a megvásárolni kívánt ingatlan vételárának 80%-a lehet, míg a havi törlesztő nem haladhatja meg a nettó jövedelem 50-60%-át.",
					},
				},
				{
					"@type": "Question",
					name: "Melyik bank kínálja a legolcsóbb lakáshitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A bankok ajánlatai folyamatosan változnak, így egyáltalán nem biztos, hogy ugyanaz a bank kínálja a legkedvezőbb feltételeket most, mint akár néhány hete. Éppen ezért fontos összehasonlítani a bankok aktuális hitelajánlatait egy naponta frissülő hitelkalkulátorral.",
					},
				},
				{
					"@type": "Question",
					name: "Önerő nélkül is kaphatok lakáshitelt?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Egy ingatlan megvásárlásakor általában a vételár 20%-a a minimálisan elvárt önerő, míg a fennmaradó részt hitelezi meg a bank. További ingatlanfedezet bevonásával azonban akár önerő nélkül is megoldható a hitellel történő vásárlás. Ilyenkor a fedezetként bevont ingatlanok összértékének 80%-áig hitelezhetnek a bankok.",
					},
				},
				{
					"@type": "Question",
					name: "Önerőként elfogadják a CSOK-ot a bankok?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A meglévő gyermek után igényelt CSOK támogatást – családi otthonteremtési kedvezmény – a bankok szinte minden esetben elfogadják önerőként, ám a vállalt gyermekekre igényelt támogatás – megelőlegező CSOK – esetében már más a helyzet. Ez utóbbit a bankoknak csak egy része fogadja el önerőként.",
					},
				},
				{
					"@type": "Question",
					name: "Hogyan kaphatok hitelt építkezéshez?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Az építési hitel egyik sajátossága, hogy a bank azt nem egy összegben utalja ki, hanem szakaszosan, az építkezés készültségi fokának megfelelően folyósítja. Amint egy építési fázis befejeződik, a banki értékbecslő helyszíni szemlét végez, és ez alapján utalja a bank a következő részösszeget. A másik egyediség, hogy a maximálisan felvehető hitelösszeget nem csak a fedezetként ajánlott telek értéke határozza meg, hanem a rá építeni kívánt felépítmény várható majdani értéke. Hiszen a banki jelzálogot a kész házra fogják bejegyezni.",
					},
				},
				{
					"@type": "Question",
					name: "Mi az a Minősített Fogyasztóbarát Lakáshitel?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A Minősített Fogyasztóbarát Lakáshitelek esetében a bankoknak számos feltételnek kell megfelelniük, emiatt sokszor kedvezőbbek a piaci lakáshiteleknél. A banki kamatfelár maximum 3,5% lehet, a leghosszabb futamidő 30 év, a kamatperiódus nem lehet rövidebb 5 évnél, a folyósítási díj pedig nem haladhatja meg a hitelösszeg 0,75%-át, de legfeljebb 150 ezer forintot. Ezen kívül az elő- és végtörlesztési díj nem lehet magasabb az elő-/végtörlesztett összeg 1%-ánál, de a lakástakarék-pénztári betétből végrehajtott elő/végtörlesztés például díjmentes.",
					},
				},
				{
					"@type": "Question",
					name: "Melyik lakáshitel a legbiztonságosabb?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Legbiztonságosabbak a futamidő végéig rögzített kamatú jelzáloghitelek, hiszen ezeknél a kamat és a havi törlesztő végig változatlan marad.",
					},
				},
				{
					"@type": "Question",
					name: "Mikor emelkedhet a lakáshitel havi törlesztője?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "A lakáshitelek havi törlesztője abban az esetben emelkedhet, ha a hitel referenciamutatója is felfelé mozdul el. A referenciamutató a változó kamatú hiteleknél általában a BUBOR, míg a hosszú kamatperiódusú hiteleknél a BIRS. Ezek a mutatók azt jelzik, hogy a bankok adott feltételekkel milyen kamat mellett adnának egymásnak hitelt, vagyis ez tekinthető a kihelyezett pénz „beszerzési árának”.",
					},
				},
				{
					"@type": "Question",
					name: "Mennyivel emelkedhet a lakáshitel kamat?",
					acceptedAnswer: {
						"@type": "Answer",
						text: "Mivel a lakáshitelek futamideje akár 30 év is lehet, nem tudható előre, hogy milyen kamatemelkedés várható. Éppen ezért érdemes szakértő segítségét kérni, aki kiszámolja, hogy egy esetleges kamatemelkedés pontosan hogyan befolyásolná a felvenni kívánt hitel havi fizetnivalóit.",
					},
				},
			],
		};
	}
}
